import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNowStrict, parseISO } from "date-fns";
import { nn } from "date-fns/locale";
import { Mail, Trash2, MailOpen, ArrowUpRight, Phone, Search, Inbox, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/admin/PageHeader";
import { Segmented } from "@/components/admin/Toolbar";
import { DetailSheet, DetailList, DetailItem } from "@/components/admin/DetailSheet";
import { StatusPill } from "@/components/admin/StatusPill";
import { ConfirmDialog } from "@/components/admin/Fields";
import { EmptyState } from "@/components/admin/states/EmptyState";
import { ErrorState } from "@/components/admin/states/ErrorState";
import { useMediaQuery } from "@/hooks/use-media-query";
import { adminDeleteMessage, adminMarkMessage, adminMessages, errorMessage } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { cn } from "@/lib/utils";

const initials = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "?";
const rel = (iso) => { try { return formatDistanceToNowStrict(parseISO(iso), { addSuffix: true, locale: nn }); } catch { return ""; } };

/** Reading pane / sheet body – shared between desktop split view and the mobile sheet. */
const MessageBody = ({ m }) => (
  <div className="space-y-6" data-testid={`message-body-${m.id}`}>
    <p className="max-w-[68ch] whitespace-pre-wrap text-[15px] leading-[1.7]">{m.message}</p>
    <DetailList>
      <DetailItem label="Frå">{m.name}</DetailItem>
      <DetailItem label="E-post"><a href={`mailto:${m.email}`} className="break-all u-link">{m.email}</a></DetailItem>
      {m.phone && <DetailItem label="Telefon"><a href={`tel:${m.phone}`} className="tabular u-link">{m.phone}</a></DetailItem>}
      <DetailItem label="Motteke"><span className="tabular">{fmtDate(m.created_at?.slice(0, 10), "d. MMMM yyyy")}</span></DetailItem>
    </DetailList>
  </div>
);

const Actions = ({ m, onMark, onDelete, marking }) => (
  <>
    <Button asChild className="h-9" data-testid={`message-reply-${m.id}`}><a href={`mailto:${m.email}?subject=${encodeURIComponent(`Re: ${m.subject}`)}`}>Svar på e-post <ArrowUpRight className="h-3.5 w-3.5" /></a></Button>
    {m.phone && <Button asChild variant="outline" className="h-9 bg-card"><a href={`tel:${m.phone}`}><Phone className="h-3.5 w-3.5" /> Ring</a></Button>}
    <Button variant="outline" className="h-9 bg-card" onClick={() => onMark(m)} disabled={marking} data-testid={`message-mark-${m.id}`}><MailOpen className="h-3.5 w-3.5" /> {m.read ? "Merk som ulest" : "Merk som lest"}</Button>
    <Button variant="ghost" className="ml-auto h-9 text-destructive hover:text-destructive" onClick={() => onDelete(m)} data-testid={`message-delete-${m.id}`}><Trash2 className="h-3.5 w-3.5" /> Slett</Button>
  </>
);

export default function AdminMessages() {
  const qc = useQueryClient();
  const isDesktop = useMediaQuery("(min-width: 1024px)", true);
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ["admin-messages"], queryFn: adminMessages });
  const [openId, setOpenId] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [filter, setFilter] = useState("alle");
  const [q, setQ] = useState("");
  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-messages"] }); qc.invalidateQueries({ queryKey: ["admin-dashboard"] }); };

  const mark = useMutation({
    mutationFn: ({ id, read }) => adminMarkMessage(id, read),
    onMutate: async ({ id, read }) => { await qc.cancelQueries({ queryKey: ["admin-messages"] }); const prev = qc.getQueryData(["admin-messages"]); qc.setQueryData(["admin-messages"], (old) => (old || []).map((m) => (m.id === id ? { ...m, read } : m))); return { prev }; },
    onError: (e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(["admin-messages"], ctx.prev); toast.error(errorMessage(e, "Kunne ikkje oppdatere")); },
    onSettled: refresh,
  });
  const del = useMutation({ mutationFn: adminDeleteMessage, onSuccess: () => { refresh(); setToDelete(null); setOpenId(null); toast.success("Meldinga er sletta"); }, onError: (e) => toast.error(errorMessage(e, "Kunne ikkje slette")) });
  const markAll = useMutation({ mutationFn: async (ids) => { for (const id of ids) await adminMarkMessage(id, true); }, onSuccess: () => { refresh(); toast.success("Alle meldingar er merkte som lesne"); }, onError: (e) => toast.error(errorMessage(e)) });

  const unread = (data || []).filter((m) => !m.read).length;
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data || []).filter((m) => filter === "alle" || !m.read).filter((m) => !needle || `${m.name} ${m.email} ${m.subject} ${m.message}`.toLowerCase().includes(needle));
  }, [data, filter, q]);
  const open = (data || []).find((m) => m.id === openId) || null;

  // Desktop: auto-select the first message so the reading pane is never empty.
  useEffect(() => { if (isDesktop && !openId && list.length) setOpenId(list[0].id); }, [isDesktop, list, openId]);

  const openMessage = (m) => { setOpenId(m.id); if (!m.read) mark.mutate({ id: m.id, read: true }); };
  const toggleRead = (m) => mark.mutate({ id: m.id, read: !m.read });

  // j/k or arrows move through the list when the reading pane has focus context
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target; if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (!list.length || toDelete) return;
      const idx = list.findIndex((m) => m.id === openId);
      if (e.key === "ArrowDown" || e.key === "j") { e.preventDefault(); openMessage(list[Math.min(list.length - 1, idx + 1)]); }
      else if (e.key === "ArrowUp" || e.key === "k") { e.preventDefault(); openMessage(list[Math.max(0, idx - 1)]); }
      else if (e.key === "u" && open) toggleRead(open);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const listPane = (
    <div className="admin-card flex min-h-0 flex-col overflow-hidden" data-testid="messages-list">
      <div className="space-y-2.5 border-b border-border p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk i meldingar…" className="h-9 rounded-xl bg-background pl-9" aria-label="Søk i meldingar" data-testid="messages-toolbar-search" />
        </div>
        <div className="flex items-center justify-between gap-2">
          <Segmented size="sm" value={filter} onChange={setFilter} items={[{ value: "alle", label: "Alle", count: data?.length ?? 0 }, { value: "uleste", label: "Uleste", count: unread }]} testId="messages-filter" />
          {unread > 0 && <Button variant="ghost" size="sm" className="h-8 gap-1.5 px-2 text-[12.5px] text-muted-foreground hover:text-foreground" onClick={() => markAll.mutate((data || []).filter((m) => !m.read).map((m) => m.id))} disabled={markAll.isPending} data-testid="messages-mark-all"><CheckCheck className="h-3.5 w-3.5" /> Merk alle lesne</Button>}
        </div>
      </div>
      {isError ? <div className="p-3"><ErrorState title="Kunne ikkje hente meldingar" text={error?.message} onRetry={refetch} /></div> : isLoading ? (
        <ul className="divide-y divide-border" aria-busy="true">{[0, 1, 2, 3, 4].map((i) => <li key={i} className="flex gap-3 px-3 py-3"><Skeleton className="h-9 w-9 rounded-full" /><div className="flex-1 space-y-2"><Skeleton className="h-3.5 w-1/2" /><Skeleton className="h-3 w-3/4" /></div></li>)}</ul>
      ) : !list.length ? (
        <EmptyState compact icon={Mail} title={q || filter !== "alle" ? "Ingen treff" : "Ingen meldingar enno"} text={q || filter !== "alle" ? "Prøv eit anna søkeord eller vis alle." : "Når nokon sender inn kontaktskjemaet på nettsida, dukkar meldinga opp her."} action={(q || filter !== "alle") ? <Button variant="outline" size="sm" className="h-9 bg-card" onClick={() => { setQ(""); setFilter("alle"); }}>Nullstill filter</Button> : null} testId="messages-list-empty" />
      ) : (
        <ul className="min-h-0 flex-1 divide-y divide-border overflow-y-auto" role="listbox" aria-label="Meldingar">
          {list.map((m) => {
            const sel = m.id === openId;
            return (
              <li key={m.id} role="option" aria-selected={sel}>
                <button type="button" onClick={() => openMessage(m)} className={cn("flex w-full items-start gap-3 px-3 py-3 text-left transition-colors hover:bg-secondary/60 focus-visible:bg-secondary/60 focus-visible:outline-none", sel && "bg-sol/[0.09] hover:bg-sol/[0.12] shadow-[inset_3px_0_0_hsl(var(--sol))]")} data-testid={`message-toggle-${m.id}`} data-unread={!m.read ? "true" : undefined}>
                  <span className={cn("relative mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold", m.read ? "bg-secondary text-foreground/80" : "bg-sol/25 text-foreground ring-1 ring-sol/50")} aria-hidden="true">
                    {initials(m.name)}
                    {!m.read && <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-sol ring-2 ring-card" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline justify-between gap-2">
                      <span className={cn("truncate text-[13.5px]", m.read ? "font-medium text-foreground/90" : "font-semibold")}>{m.name}</span>
                      <span className="shrink-0 text-[11.5px] text-muted-foreground tabular">{rel(m.created_at)}</span>
                    </span>
                    <span className={cn("mt-0.5 block truncate text-[13px]", m.read ? "text-foreground/80" : "font-medium text-foreground")}>{m.subject}</span>
                    <span className="mt-0.5 block truncate text-[12px] text-muted-foreground">{m.message}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  return (
    <div>
      <PageHeader title="Meldingar" description={`Meldingar frå kontaktskjemaet på nettsida.${unread ? ` ${unread} ulest${unread === 1 ? "" : "e"}.` : " Alt er lese."}`} status={unread > 0 && <StatusPill status="ulest" label={`${unread} ulest${unread === 1 ? "" : "e"}`} />} />

      {isDesktop ? (
        <div className="grid min-h-[560px] gap-4 lg:grid-cols-[380px_minmax(0,1fr)]" style={{ height: "calc(100vh - 230px)" }} data-testid="messages-split">
          {listPane}
          <section className="admin-card flex min-h-0 flex-col overflow-hidden" aria-label="Lesing" data-testid="message-reading-pane">
            {!open ? (
              <div className="flex flex-1 flex-col items-center justify-center p-10 text-center" data-testid="message-reading-empty">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary text-muted-foreground"><Inbox className="h-5 w-5" strokeWidth={1.75} /></span>
                <p className="mt-3 text-sm font-medium">Vel ei melding</p>
                <p className="mt-1 max-w-xs text-[13px] text-muted-foreground">Bruk ↑ ↓ for å bla, U for å merkje som ulest.</p>
              </div>
            ) : (
              <>
                <header className="border-b border-border px-6 py-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="t-mono text-muted-foreground">Motteke {fmtDate(open.created_at?.slice(0, 10), "d. MMMM yyyy")} · {rel(open.created_at)}</p>
                      <h2 className="mt-1.5 font-display text-[22px] font-semibold leading-tight tracking-[-0.01em]" data-testid="message-reading-subject">{open.subject}</h2>
                      <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13.5px] text-muted-foreground">
                        <span className="inline-flex items-center gap-2 text-foreground"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-sol/25 text-[10px] font-semibold">{initials(open.name)}</span>{open.name}</span>
                        <a href={`mailto:${open.email}`} className="u-link">{open.email}</a>
                        {open.phone && <a href={`tel:${open.phone}`} className="tabular u-link">{open.phone}</a>}
                      </p>
                    </div>
                    <StatusPill status={open.read ? "lest" : "ulest"} />
                  </div>
                </header>
                <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6"><MessageBody m={open} /></div>
                <footer className="flex flex-wrap items-center gap-2 border-t border-border px-6 py-3"><Actions m={open} onMark={toggleRead} onDelete={setToDelete} marking={mark.isPending} /></footer>
              </>
            )}
          </section>
        </div>
      ) : (
        <>
          {listPane}
          <DetailSheet open={!!open} onOpenChange={(o) => !o && setOpenId(null)} testId="message-detail-sheet" eyebrow={open ? `Motteke ${fmtDate(open.created_at?.slice(0, 10), "d. MMMM yyyy")}` : undefined} status={open && <StatusPill status={open.read ? "lest" : "ulest"} />} title={open?.subject || ""} description={open ? `Frå ${open.name}` : undefined} footer={open && <Actions m={open} onMark={toggleRead} onDelete={setToDelete} marking={mark.isPending} />}>
            {open && <MessageBody m={open} />}
          </DetailSheet>
        </>
      )}

      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} title="Slette meldinga?" text={`Meldinga frå ${toDelete?.name || ""} vert sletta for godt.`} confirmLabel="Slett" destructive loading={del.isPending} onConfirm={() => del.mutate(toDelete.id)} testId="confirm-delete-message-dialog" />
    </div>
  );
}
